import React from 'react';
import { ShieldCheck } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';

const TONES = {
  senior: { text: 'text-amber-800', box: 'border-amber-200 bg-amber-50', btn: 'bg-amber-500 hover:bg-amber-600' },
  jahres: { text: 'text-emerald-700', box: 'border-emerald-200 bg-emerald-50', btn: 'bg-emerald-600 hover:bg-emerald-700' },
  css: { text: 'text-fuchsia-700', box: 'border-fuchsia-200 bg-fuchsia-50', btn: 'bg-fuchsia-600 hover:bg-fuchsia-700' },
  member: { text: 'text-amber-700', box: 'border-amber-200 bg-amber-50', btn: 'bg-amber-500 hover:bg-amber-600' },
  abo: { text: 'text-emerald-700', box: 'border-emerald-200 bg-emerald-50', btn: 'bg-emerald-500 hover:bg-emerald-600' },
};

const fractionLabel = (f, L) => Math.abs(f - 0.25) < 0.01 ? '1/4' : Math.abs(f - 0.5) < 0.01 ? '1/2' : L('Full', 'Ganz');

const buildContent = ({ kind, item, fraction, sub }, L) => {
  const base = item.original_total || item.total || 0;
  const open = (item.total || 0) - (item.paid_amount || 0);
  const share = Math.min(base * (fraction || 1), open);
  const chf = (v) => `${v.toFixed(2)} CHF`;
  if (kind === 'senior') return {
    title: L('Really apply Senior Abo?', 'Senior-Abo wirklich anwenden?'),
    note: L('Share is marked as paid — NO cash register entry.', 'Anteil wird als bezahlt markiert — KEIN Kassen-Eintrag.'),
    rows: [[L('Share', 'Anteil'), fractionLabel(fraction, L)], [L('Amount', 'Betrag'), chf(share)]],
  };
  if (kind === 'jahres') return {
    title: L('Really apply Yearly Abo?', 'Jahres-Abo wirklich anwenden?'),
    note: L('Share is marked as paid — NO cash register entry.', 'Anteil wird als bezahlt markiert — KEIN Kassen-Eintrag.'),
    rows: [[L('Share', 'Anteil'), fractionLabel(fraction, L)], [L('Amount', 'Betrag'), chf(share)]],
  };
  if (kind === 'css') return {
    title: L('Really apply CSS Coins?', 'CSS Coins wirklich anwenden?'),
    note: L('Share is marked as paid — not counted in daily till.', 'Anteil wird als bezahlt markiert — nicht im Tages-Total.'),
    rows: [[L('Share', 'Anteil'), fractionLabel(fraction, L)], [L('Amount', 'Betrag'), chf(share)]],
  };
  if (kind === 'member') return {
    title: L('Really apply member discount?', 'Member-Rabatt wirklich anwenden?'),
    note: L('20% off the 1/4 share of the court fee.', '20% Rabatt auf den 1/4-Anteil der Platzgebühr.'),
    rows: [[L('Share', 'Anteil'), '1/4'], [L('Discount', 'Rabatt'), `-${chf(Math.min(base * 0.25 * 0.2, open))}`]],
  };
  const aboName = sub.type === 'training' ? L('Training Abo', 'Training-Abo') : L('Court Abo', 'Court-Abo');
  return {
    title: L(`Really use ${aboName}?`, `${aboName} wirklich verwenden?`),
    note: L('The booking price is deducted from the abo balance.', 'Der Buchungspreis wird vom Abo-Restwert abgezogen.'),
    rows: [[L('Subscription', 'Abo'), aboName], [L('Balance', 'Restwert'), chf(sub.balance_remaining ?? 0)], [L('Deducted', 'Wird abgezogen'), chf(open)]],
  };
};

export const PayConfirmDialog = ({ confirm, customerName, loading, onCancel, onConfirm, L }) => {
  const tone = TONES[confirm?.kind] || TONES.abo;
  const content = confirm ? buildContent(confirm, L) : null;
  return (
    <Dialog open={!!confirm} onOpenChange={(o) => { if (!o && !loading) onCancel(); }}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-sm rounded-2xl" data-testid="pay-confirm-dialog">
        {content && (
          <>
            <DialogHeader>
              <DialogTitle className={`text-lg font-bold flex items-center gap-2 ${tone.text}`} data-testid="pay-confirm-title">
                <ShieldCheck size={22} weight="duotone" /> {content.title}
              </DialogTitle>
              <DialogDescription className="text-slate-500">{content.note}</DialogDescription>
            </DialogHeader>
            <div className={`rounded-xl border p-4 space-y-2 text-sm ${tone.box}`} data-testid="pay-confirm-details">
              {[[L('Customer', 'Kunde'), customerName || '—'], [L('Item', 'Position'), confirm.item.name], ...content.rows].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3">
                  <span className="text-slate-500">{k}</span>
                  <span className="font-semibold text-slate-900 text-right">{v}</span>
                </div>
              ))}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onCancel} disabled={loading} className="flex-1 border-slate-200 text-slate-600" data-testid="pay-confirm-cancel">
                {L('Cancel', 'Abbrechen')}
              </Button>
              <Button onClick={onConfirm} disabled={loading} className={`flex-1 text-white font-bold ${tone.btn}`} data-testid="pay-confirm-yes">
                {loading ? L('Applying…', 'Wird angewendet…') : L('Yes, apply', 'Ja, anwenden')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
